import React from "react";
import moment from "moment-timezone";
import logo from "../../assets/logo.png";
import { MEMO_AREA_LABELS } from "../../constants/memoAreas";

const formatDate = (value) => {
  if (!value) return "N/A";
  return moment(value).format("MMMM DD, YYYY");
};

const formatTime = (value) => {
  if (!value) return "";
  return moment(value).tz("America/Toronto").format("h:mm A");
};

const formatDateShort = (value) => {
  if (!value) return "";
  return moment(value).format("DD.MM.YYYY");
};

const JobCompletionReportPdfTemplate = ({ report }) => {
  if (!report) return null;

  const importantMemos = report.days.flatMap((day) =>
    day.employeeLogs.flatMap((employee) =>
      employee.memos
        .filter((memo) => memo.important)
        .map((memo) => ({
          ...memo,
          employeeName: employee.employeeName,
        }))
    )
  );

  const locations = report.locations?.length
    ? report.locations.join(", ")
    : "N/A";

  return (
    <div className="report-pdf-template">
      <style>{`
        .report-pdf-template {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 10pt;
          color: #000;
          background: #fff;
          width: 8.5in;
          margin: 0 auto;
        }

        .front-page {
          page-break-after: always;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30pt 50pt;
          min-height: 100vh;
        }

        .front-title {
          font-size: 22pt;
          font-weight: 800;
          text-align: center;
          margin-bottom: 40pt;
          line-height: 1.4;
          color: #000;
        }

        .front-logo-container {
          margin-bottom: 80pt;
          width: 280pt;
          text-align: center;
        }

        .front-logo-container img {
          max-width: 100%;
          height: auto;
        }

        .front-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: auto;
        }

        .front-table th,
        .front-table td {
          border: 1px solid #777;
          padding: 12pt;
          font-size: 11pt;
          text-align: left;
        }

        .front-table th {
          background: #000;
          color: #fff;
          font-weight: bold;
        }

        .front-table td.label-cell {
          background: #cccccc;
          font-weight: bold;
          width: 35%;
        }

        .page-shell {
          padding: 30pt 50pt;
        }

        .job-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 20px;
          margin-bottom: 10px;
          border-bottom: 2px solid #000;
          padding-bottom: 14px;
        }

        .summary {
          border: 1px solid #000;
          border-radius: 8px;
          padding: 10px;
          margin-top: 10px;
          margin-bottom: 14px;
          background: #fafafa;
        }

        .summary-title {
          font-weight: bold;
          font-size: 11pt;
          margin-bottom: 8px;
          text-transform: none;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 20px;
        }

        .day-section {
          margin: 10pt 0pt;
          padding: 30pt 10px;
        }

        .day-section h2 {
          margin: 0 0 8px;
          font-size: 12pt;
        }

        .employee-section {
          border-top: 1px solid #e4e4e4;
          padding-top: 10px;
          margin-top: 10px;
        }

        .employee-section:first-of-type {
          border-top: none;
          margin-top: 0;
          padding-top: 0;
        }

        .employee-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 9.5pt;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8pt;
          table-layout: fixed;
        }

        th, td {
          border: 1px solid #000;
          padding: 6px;
          text-align: left;
          vertical-align: top;
          word-break: break-word;
          font-size: 9pt;
        }

        th {
          background: #f0f0f0;
          font-weight: bold;
        }

        /* Table Column Widths */
        table th:nth-child(1) { width: 50pt; }  /* Time */
        table th:nth-child(2) { width: 70pt; }  /* Area */
        table th:nth-child(3) { width: auto; }  /* Memo */
        table th:nth-child(4) { width: 120pt; } /* Attachments */

        .important-logs {
          margin-top: 14px;
          margin-bottom: 20px;
          padding: 10px;
          border: 1px solid #000;
          border-radius: 8px;
          background: #fff;
        }

        .important-logs-title {
          font-weight: bold;
          color: #000;
          margin-bottom: 8px;
          font-size: 11pt;
          text-decoration: underline;
        }

        .important-logs ol {
          margin: 0;
          padding-left: 20px;
        }

        .important-logs li {
          margin-bottom: 6px;
          font-size: 9.5pt;
        }

        .portal-qr-container {
          display: flex;
          justify-content: center;
          margin: 10pt 0;
        }

        .qr-box {
          border: 1px solid #000;
          padding: 10px;
          border-radius: 8px;
          text-align: center;
          background: #f9f9f9;
          width: 120px;
        }

        .qr-box img {
          width: 80px;
          height: 80px;
          margin-bottom: 6px;
        }

        .qr-label {
          font-size: 8pt;
          font-weight: bold;
          line-height: 1.2;
        }

        @media print {
          .front-page { page-break-after: always; }
          .day-section { page-break-inside: avoid; }
          tr { page-break-inside: avoid; }
        }
      `}</style>

      {/* Front Page */}
      <div className="front-page">
        <div className="front-title">
          PROFESSIONAL JOB COMPLETION
          <br />
          REPORT
        </div>

        <div className="front-logo-container">
          <img src={logo} alt="logo" />
        </div>

        <table className="front-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="label-cell">{report.isContract ? "Contract ID" : "Job ID"}</td>
              <td className="value-cell">{report.jobId}</td>
            </tr>
            <tr>
              <td className="label-cell">Client Name</td>
              <td className="value-cell">{report.organizationName}</td>
            </tr>
            <tr>
              <td className="label-cell">Property/Site Address</td>
              <td className="value-cell">{locations}</td>
            </tr>
            <tr>
              <td className="label-cell">Reporting Period</td>
              <td className="value-cell">
                {formatDate(report.startDate)} - {formatDate(report.endDate)}
              </td>
            </tr>
            <tr>
              <td className="label-cell">A2Z Team Lead/Supervisor</td>
              <td className="value-cell"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Content Pages */}
      <div className="page-shell">
        <div className="job-meta">
          <div>
            <strong>{report.isContract ? "Contract ID:" : "Job ID:"}</strong> {report.jobId}
          </div>
          <div>
            <strong>Generated:</strong> {formatDate(report.generatedAt)}
          </div>
          <div>
            <strong>Organization:</strong> {report.organizationName}
          </div>
          <div>
            <strong>PO Request Location:</strong> {report.poRequestLocation}
          </div>
          <div>
            <strong>Start Date:</strong> {formatDate(report.startDate)}
          </div>
          <div>
            <strong>End Date:</strong> {formatDate(report.endDate)}
          </div>
        </div>

        <div className="summary">
          <div className="summary-title">Summary</div>
          <div className="summary-grid">
            <div>Total Execution Days: {report.summary.totalExecutionDays}</div>
            <div>Total Employees: {report.summary.totalEmployees}</div>
            <div>Total Memo Updates: {report.summary.totalMemos}</div>
            <div>Total Attachments: {report.summary.totalAttachments}</div>
            <div>
              Total Human Hours Performed:{" "}
              {report.summary.totalHumanHoursPerformed}
            </div>
            <div>
              Total Performed Seconds: {report.summary.totalPerformedSeconds}
            </div>
          </div>
        </div>

        {/* Job Attachments Portal QR Code */}
        {report.jobAttachmentsPortalUrl && (
          <div className="portal-qr-container">
            <div className="qr-box">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                  report.jobAttachmentsPortalUrl
                )}`}
                alt="Job Portal QR"
              />
              <div className="qr-label">
                Scan to view all
                <br />
                job attachments
              </div>
            </div>
          </div>
        )}

        {importantMemos.length > 0 && (
          <div className="important-logs">
            <div className="important-logs-title">
              Important Logs During the Contract Period
            </div>
            <ol>
              {importantMemos.map((memo, idx) => (
                <li key={idx}>
                  <strong>
                    {formatDateShort(memo.timestamp)} {formatTime(memo.timestamp)}{" "}
                    : {memo.employeeName}:
                  </strong>{" "}
                  {memo.content}{" "}
                  {memo.attachments?.length > 0
                    ? `- ${memo.attachments.map((a) => a.filename).join(", ")}`
                    : ""}
                </li>
              ))}
            </ol>
          </div>
        )}

        {report.days
          .filter((section) => section.employeeLogs.length > 0)
          .map((section, idx) => (
            <div key={idx} className="day-section">
              <h2>{formatDate(section.dateKey)}</h2>
              {section.employeeLogs.map((employee, eIdx) => (
                <div key={eIdx} className="employee-section">
                  <div className="employee-header">
                    <div>
                      <strong>Employee:</strong> {employee.employeeName}
                    </div>
                    <div>
                      <strong>Performed Hours:</strong>{" "}
                      {employee.performedHours}
                    </div>
                  </div>
                  <div className="sub-section">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: "80pt" }}>Time</th>
                          <th style={{ width: "120pt" }}>Area</th>
                          <th>Memo</th>
                          <th style={{ width: "150pt" }}>Attachments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employee.memos.length > 0 ? (
                          employee.memos.map((memo, mIdx) => (
                            <tr key={mIdx}>
                              <td>{formatTime(memo.timestamp)}</td>
                              <td>
                                {memo.type === "memo"
                                  ? MEMO_AREA_LABELS[memo.locationArea] || "-"
                                  : "Others"}
                              </td>
                              <td>
                                {memo.type === "memo"
                                  ? memo.content || "-"
                                  : memo.type === "gpsCheckOut"
                                  ? "Left the Job Location (Auto)"
                                  : memo.type === "gpsCheckIn"
                                  ? "Entered the Job Location (Auto)"
                                  : memo.type === "checkOut"
                                  ? "Checked Out"
                                  : "Checked In"}
                              </td>
                              <td>
                                {memo.attachments?.length > 0
                                  ? memo.attachments
                                      .map((a) => a.filename)
                                      .join(", ")
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>
                              No memo updates on this day.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

export default JobCompletionReportPdfTemplate;
